import { Download, ClipboardCheck } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const GROUPS = [
  {
    title: "Power & electrical",
    items: [
      "Nearby outlet and its circuit capacity",
      "Whether a dedicated circuit is needed (micro markets)",
      "Voltage & amperage at the spot",
    ],
  },
  {
    title: "Space & fit",
    items: [
      "Footprint dimensions for the machine or cooler",
      "Door clearance, aisle width & accessibility",
      "Level flooring — machines need it within tolerance",
    ],
  },
  {
    title: "Access & delivery",
    items: [
      "Delivery path: doors, hallways, stairs or elevator",
      "Loading spot close to the entrance",
    ],
  },
  {
    title: "Connectivity",
    items: [
      "Cellular signal for telemetry & cashless payments",
      "Wi-Fi availability as an optional backup",
    ],
  },
  {
    title: "Traffic & menu fit",
    items: [
      "Team size and peak break times",
      "Existing food options nearby, to design your menu",
    ],
  },
];

export default function SurveyChecklist() {
  return (
    <section
      id="survey"
      data-testid="survey-checklist-section"
      className="relative py-24 lg:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal>
            <Eyebrow>Free site survey</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#16120E] sm:text-4xl lg:text-5xl">
              Know exactly what we check.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-stone-600 sm:text-lg">
              Before anything is promised, we walk your space and run this
              checklist — power, space, access, signal and fit. It takes 30
              minutes, costs nothing, and if a spot needs extra electrical or
              construction work, you hear it from us first, in plain numbers.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                data-testid="survey-book-button"
                onClick={() => scrollToSection("quote")}
                className="inline-flex items-center gap-2.5 rounded-full bg-[#1B365D] px-6 py-3.5 font-semibold text-[#FBF7EF] transition-all hover:-translate-y-0.5 hover:bg-[#2A4A73] hover:shadow-[0_14px_40px_rgba(27,54,93,0.35)]"
              >
                <ClipboardCheck className="h-5 w-5" style={{ width: 18, height: 18 }} />
                Book my free survey
              </button>
              <a
                data-testid="survey-pdf-link"
                href="/site-survey-checklist.pdf"
                download
                className="inline-flex items-center gap-2.5 rounded-full border border-[#C9A227]/50 px-6 py-3.5 font-semibold text-[#A8861D] transition-all hover:-translate-y-0.5 hover:border-[#C9A227] hover:bg-[#C9A227]/10"
              >
                <Download className="h-5 w-5" style={{ width: 18, height: 18 }} />
                Download the checklist (PDF)
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="survey-checklist-card"
            className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_30px_90px_rgba(27,54,93,0.1)]"
          >
            <div className="flex items-center justify-between border-b border-black/10 bg-[#1B365D] px-6 py-4">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FBF7EF]">
                Site survey checklist
              </span>
              <span className="rounded-full bg-[#C9A227]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#E5C558]">
                30 min · free
              </span>
            </div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 px-6 py-6 sm:grid-cols-2 sm:px-7">
              {GROUPS.map((g) => (
                <div key={g.title}>
                  <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#A8861D]">
                    {g.title}
                  </h3>
                  <ul className="mt-3 space-y-2.5">
                    {g.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-stone-600">
                        <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-[4px] border-2 border-[#1B365D]/35" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
